import { Node } from "@tiptap/core";
import { VueNodeViewRenderer } from "@tiptap/vue-3";
import SlideDeckComponent from "./SlideDeckComponent.vue";
import { mergeAttributes } from "@tiptap/core";

export const SlideDeck = Node.create({
  name: "slideDeck",

  group: "block",

  content: "slideNotes",

  isolating: true,

  selectable: true,

  draggable: true,

  addOptions() {
    return {
      HTMLAttributes: {
        class: "slide-deck-container",
      },
    };
  },

  addAttributes() {
    return {
      thumbnailUrl: {
        default: null,
      },
      slideIndex: {
        default: 0,
      },
      notes: {
        default: "",
      },
      title: {
        default: "",
      },
      blockId: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-block-id"),
        renderHTML: (attributes) => {
          return { "data-block-id": attributes.blockId };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "div.slide-deck-container",
      },
    ];
  },

  renderHTML({ HTMLAttributes, node }) {
    // Create a structure that works well outside the editor
    const thumbnailContent = [];

    // Add image element if thumbnailUrl exists
    if (node.attrs.thumbnailUrl) {
      thumbnailContent.push([
        "img",
        {
          src: node.attrs.thumbnailUrl,
          alt: `Slide ${node.attrs.slideIndex + 1}`,
          class: "slide-thumbnail-img",
        },
      ]);
    }

    // Add title element
    thumbnailContent.push([
      "div",
      { class: "slide-deck-title" },
      node.attrs.title || `Slide ${node.attrs.slideIndex + 1}`,
    ]);

    return [
      "div",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        "data-type": "slide-deck",
        "data-slide-index": node.attrs.slideIndex || 0,
        "data-block-id": node.attrs.blockId,
        class: "slide-deck-container",
      }),
      [
        "div",
        { class: "slide-deck-content" },
        [
          "div",
          {
            class: "slide-deck-thumbnail",
            "data-action": "view-slide",
            "data-thumbnailurl": node.attrs.thumbnailUrl || "",
            "data-title":
              node.attrs.title || `Slide ${node.attrs.slideIndex + 1}`,
          },
          ...thumbnailContent,
        ],
        ["div", { class: "slide-deck-info" }, 0],
      ],
    ];
  },

  addNodeView() {
    return VueNodeViewRenderer(SlideDeckComponent);
  },

  addCommands() {
    return {
      insertSlideDeck:
        (attributes) =>
        ({ chain, state, dispatch }) => {
          // Create a new slideDeck with default content
          const noteContent = attributes.notes
            ? [
                {
                  type: "paragraph",
                  content: [{ type: "text", text: attributes.notes }],
                },
              ]
            : [
                {
                  type: "paragraph",
                  content: [{ type: "text", text: "Add slide notes here..." }],
                },
              ];

          // Insert the slide deck as a top-level node
          const result = chain()
            .insertContent({
              type: this.name,
              attrs: attributes,
              content: [
                {
                  type: "slideNotes",
                  content: noteContent,
                },
              ],
            })
            .run();

          // After insertion, move selection to the end of the document to prevent nesting
          if (result && dispatch) {
            // Find the position after the node we just inserted
            const { selection } = state;
            const $pos = state.doc.resolve(selection.to);

            // If we're inside a slideDeck node, move cursor outside and after it
            if (
              $pos.parent.type.name === "slideNotes" ||
              $pos.parent.type.name === "slideDeck"
            ) {
              const endPos = $pos.after(1); // Get position after the slideDeck node
              const newTr = state.tr.setSelection(
                state.selection.constructor.near(state.doc.resolve(endPos))
              );
              dispatch(newTr);
            }
          }

          return result;
        },
    };
  },
});
